import React from 'react';
import { AppData } from '../types';
import { Check, ExternalLink, ShieldCheck, Smartphone, Layers, Globe, Clock, Tag } from 'lucide-react';
import { useI18n } from '../../services/i18n';

interface AppStoreTabProps {
  app: AppData;
}

export const AppStoreTab: React.FC<AppStoreTabProps> = ({ app }) => {
  const { t } = useI18n();

  const metadataFields = [
    { label: 'App Store ID', value: String(app.appStoreId || 'N/A'), isMono: true },
    { label: 'Bundle Identifier', value: app.bundleId || 'N/A', isMono: true },
    { label: 'Primary Category', value: app.category || 'Utilities' },
    { label: 'Genres', value: app.tags?.join(', ') || app.category },
    { label: 'Developer', value: app.developer },
    { label: 'Developer Country', value: app.country || 'Global / United States' },
    { label: 'Current Version', value: app.versions?.[0]?.version || '1.0.0' },
    { label: 'First Release Date', value: app.firstReleaseDate ? new Date(app.firstReleaseDate).toLocaleDateString() : 'Available on App Store' },
    { label: 'Last Version Release', value: app.lastUpdated ? new Date(app.lastUpdated).toLocaleDateString() : 'Recent' },
    { label: 'Price & Monetization', value: app.price === 'Free' ? 'Free (with In-App Purchases)' : app.price },
    { label: 'Store Currency', value: 'USD ($)' },
    { label: 'Minimum iOS Version', value: `iOS ${app.minimumOsVersion || '15.0'} or later` },
    { label: 'Content Advisory Rating', value: app.contentRating || '4+' },
    { label: 'Bundle Size', value: app.fileSize || 'Approx. 85 MB' }
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Data Source Provenance Card */}
      <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-black uppercase tracking-wider text-emerald-700 bg-emerald-100/60 px-2 py-0.5 rounded-full">
                VERIFIED SOURCE
              </span>
              <span className="text-xs text-slate-400">Direct Ingestion</span>
            </div>
            <h3 className="text-base font-bold text-slate-900 mt-0.5">
              Official Apple Public Metadata
            </h3>
            <p className="text-xs text-slate-500">
              Directly queried and indexed from the Apple App Store Public Catalog API.
            </p>
          </div>
        </div>

        {app.appStoreUrl && (
          <a
            href={app.appStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2.5 rounded-2xl text-xs font-bold bg-slate-900 hover:bg-black text-white transition-all flex items-center gap-2 shadow-xs flex-shrink-0 self-start sm:self-center"
          >
            <span>{t('profile.viewOnAppStore')}</span>
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={2} />
          </a>
        )}
      </div>

      {/* Metadata Grid */}
      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs space-y-6">
        <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
          Storefront Technical Specifications
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {metadataFields.map((field) => (
            <div key={field.label} className="p-4 rounded-2xl bg-slate-50/80 border border-slate-100 space-y-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                {field.label}
              </span>
              <span className={`text-sm font-semibold text-slate-900 block truncate ${field.isMono ? 'font-mono text-xs' : ''}`}>
                {field.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Latest Release Notes */}
      {app.versions && app.versions.length > 0 && (
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-wider">
              Latest Version Release Notes ({app.versions[0].version})
            </h4>
            <span className="text-xs text-slate-400 font-medium">
              {app.versions[0].releaseDate ? new Date(app.versions[0].releaseDate).toLocaleDateString() : ''}
            </span>
          </div>
          <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 text-xs text-slate-700 whitespace-pre-line leading-relaxed font-mono">
            {app.versions[0].releaseNotes || 'Maintenance update and bug fixes.'}
          </div>
        </div>
      )}
    </div>
  );
};
